import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Admin from './Admin';
import './App.css';

const DISCORD_INVITE = "https://discord.gg/dnXeJdV59b"; 
const API_URL = "https://vrx-website.onrender.com"; 

setInterval(async () => { try { await fetch(API_URL); } catch (err) {} }, 5 * 60 * 1000); 

// --- SKELETON COMPONENT ---
const SkeletonCard = () => (
  <div className="creator-card-new skeleton-wrapper">
    <div className="creator-img-wrapper skeleton"></div>
    <div className="creator-info">
        <div className="skeleton-title skeleton"></div>
        <div className="skeleton-subtitle skeleton"></div>
    </div>
  </div>
);

// --- YOUTUBE URL PARSER ---
const getEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
        let videoId = '';
        if (url.includes('youtu.be/')) videoId = url.split('youtu.be/')[1].split('?')[0];
        else if (url.includes('v=')) videoId = url.split('v=')[1].split('&')[0];
        if (videoId) return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&modestbranding=1&loop=1&playlist=${videoId}`;
    }
    return url; 
};

// --- FRAMER MOTION VARIANTS ---
const pageVariants = {
  initial: { opacity: 0, y: -20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  exit: { opacity: 0, transition: { duration: 0.2, ease: "easeInOut" } } 
};

const staggerContainer = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
};

const timeAgo = (dateString) => {
    if(!dateString) return "Recently";
    const date = new Date(dateString);
    const seconds = Math.floor((new Date() - date) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + " years ago";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + " months ago";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " days ago";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " hours ago";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " minutes ago";
    return Math.floor(seconds) + " seconds ago";
};

// ==========================================
// HOME PAGE COMPONENT
// ==========================================
function Home({ data, loading }) {
  const [formData, setFormData] = useState({ name: '', link: '', email: '' });
  const [btnText, setBtnText] = useState("INITIALIZE UPLOAD");
  const [theme, setTheme] = useState('dark');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setTheme(systemPrefersDark ? 'dark' : 'light');
  }, []);

  useEffect(() => {
    if (theme === 'light') document.body.classList.add('light-mode');
    else document.body.classList.remove('light-mode');
  }, [theme]);

  const toggleTheme = () => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault();
    setBtnText("OPENING SECURE CHANNEL...");
    window.open(data.general?.discordLink || DISCORD_INVITE, '_blank');
    try { await axios.post(`${API_URL}/api/apply`, formData); setBtnText("UPLOAD SUCCESSFUL"); setFormData({ name: '', link: '', email: '' }); setTimeout(() => setBtnText("INITIALIZE UPLOAD"), 3000); } catch (error) { setBtnText("CONNECTION FAILED"); }
  };

  const ownerList = data.owners || [];
  const collabList = data.collaborators || [];
  const eventList = data.events || [];
  const general = data.general || {}; 
  const navHeight = general.newsTicker ? 105 : 70; 

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false); 
  };

  return (
    <motion.div className="app-container" variants={pageVariants} initial="initial" animate="animate" exit="exit" style={{ paddingTop: `${navHeight}px` }}>
      
      {general.newsTicker && (
        <div className="news-ticker"><div className="ticker-content"><span>⚡ {general.newsTicker} ⚡</span><span>⚡ {general.newsTicker} ⚡</span><span>⚡ {general.newsTicker} ⚡</span></div></div>
      )}

      <nav className="navbar" style={{top: general.newsTicker ? '35px' : '0'}}>
        <div className="logo">VORTEX<span style={{color: '#ff0000'}}> </span>ORIGINALS<div className="live-dot"></div></div>
        
        <div className={`hamburger ${isMenuOpen ? 'active' : ''}`} onClick={() => setIsMenuOpen(!isMenuOpen)}>
           <span></span><span></span><span></span>
        </div>

        <div className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
          <span onClick={() => scrollToSection('vision')}>VISION</span>
          <span onClick={() => { navigate('/roster'); setIsMenuOpen(false); }}>CREATORS</span>
          <span onClick={() => { navigate('/partners'); setIsMenuOpen(false); }}>PARTNERS</span>
          {eventList.length > 0 && <span onClick={() => scrollToSection('events')} style={{color: 'yellow'}}>EVENTS</span>}
          <span onClick={() => scrollToSection('management')}>COMMAND</span>
          <div className="nav-socials">
            <a href={general.discordLink || DISCORD_INVITE} target="_blank" rel="noreferrer"><i className="fab fa-discord"></i></a>
            <a href="https://twitter.com/VRXORIGINALS" target="_blank" rel="noreferrer"><i className="fab fa-twitter"></i></a>
            <a href="https://instagram.com/vrxoriginals" target="_blank" rel="noreferrer"><i className="fab fa-instagram"></i></a>
          </div>
          <button onClick={toggleTheme} className="theme-btn" title="Toggle Theme">{theme === 'dark' ? '☀' : '☾'}</button>
          <span onClick={() => scrollToSection('apply')} className="nav-join-btn">JOIN PROTOCOL</span>
        </div>
      </nav>

      <header className="hero" style={{minHeight: `calc(100vh - ${navHeight}px)`}}>
        <motion.div className="hero-content" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2 }}>
          <h1 className="glitch" data-text={general.heroTitle || "VORTEX"}>{general.heroTitle || "VORTEX"} <br/> <span style={{color: 'var(--text-main)', textShadow: '0 0 20px #ff0000'}}>{general.heroHighlight || "ORIGINALS"}</span></h1>
          <div className="sub-hero">{general.subHero || "DEFINING THE META"}</div>
        </motion.div>
        
        <div className="stats-bar">
            <div className="stat-item"><span className="stat-num" style={{color: '#00ff00', textShadow: '0 0 10px #00ff00'}}>{general.statMembers || "ONLINE"}</span><span className="stat-label">SYSTEM STATUS</span></div><div className="stat-divider"></div>
            <div className="stat-item"><span className="stat-num" style={{color: '#ff0000', textShadow: '0 0 10px #ff0000'}}>{general.statWins || "ACTIVE"}</span><span className="stat-label">RECRUITMENT</span></div><div className="stat-divider"></div>
            <div className="stat-item"><span className="stat-num" style={{color: '#00ffff', textShadow: '0 0 10px #00ffff'}}>{general.statTourneys || "GLOBAL"}</span><span className="stat-label">OPERATION REGION</span></div>
        </div>
        
        <motion.a href="#apply" className="hero-btn" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.8 }} onClick={(e) => { e.preventDefault(); scrollToSection('apply'); }}>JOIN THE VORTEX</motion.a>
      </header>

      {/* EVENTS */}
      {eventList.length > 0 && (
        <section id="events" style={{padding: '100px 20px'}}>
           <div className="section-title" style={{borderColor: 'yellow', textShadow: '0 0 20px yellow'}}>UPCOMING EVENTS</div>
           <div className="card-grid">
              {eventList.map((ev) => (
                <motion.div key={ev.id} className="event-card" whileHover={{ y: -10 }} onClick={() => window.open(ev.link, '_blank')}>
                   <div className="img-box"><img src={ev.img} alt={ev.name} /></div>
                   <div className="info-box owner-info"><h3 style={{color: 'yellow'}}>{ev.name}</h3><span className="role-tag" style={{color: 'var(--text-main)'}}>{ev.date}</span><div className="click-hint" style={{color: 'yellow', opacity: 1}}>CLICK TO WATCH</div></div>
                </motion.div>
              ))}
           </div>
        </section>
      )}

      <section id="management" style={{padding: '100px 20px'}}>
        <div className="section-title" style={{WebkitTextStroke: '1px #ff0000', borderColor: '#ff0000'}}>VRX COMMAND</div>
        <div className="card-grid">
          {loading ? (
             <SkeletonCard />
          ) : ownerList.map((owner) => (
            <motion.div key={owner.id} className="owner-card" whileHover={{ y: -10 }} onClick={() => window.open(owner.link, '_blank')}>
              <div className="img-box"><img src={owner.img} alt={owner.name} /></div>
              <div className="info-box owner-info"><h3>{owner.name}</h3><span className="role-tag owner-tag">{"//"} {owner.role}</span></div>
            </motion.div>
          ))}
        </div>
      </section>

      {collabList.length > 0 && (
         <section id="collabs" style={{padding: '50px 20px'}}>
            <div className="section-title" style={{fontSize: '24px', borderColor: 'cyan', textShadow: '0 0 20px cyan'}}>OFFICIAL FRIENDS</div>
            <div className="card-grid">
               {collabList.map((c) => (<motion.div key={c.id} className="owner-card" style={{width: '200px', borderColor: 'cyan'}} whileHover={{ y: -5 }} onClick={() => window.open(c.link, '_blank')}><div className="img-box" style={{height: '200px'}}><img src={c.img} alt={c.name} /></div><div className="info-box"><h3 style={{fontSize: '18px'}}>{c.name}</h3></div></motion.div>))}
            </div>
         </section>
      )}

      <section id="apply">
        <div className="section-title">JOIN PROTOCOL</div>
        <div className="form-wrapper">
          <form onSubmit={handleSubmit}>
            <input name="name" placeholder="CODENAME (IGN)" value={formData.name} onChange={handleChange} required />
            <input name="link" placeholder="COMMS FREQUENCY (LINK)" value={formData.link} onChange={handleChange} required />
            <input name="email" placeholder="CONTACT POINT (EMAIL)" value={formData.email} onChange={handleChange} required />
            <button className="glitch-btn">{btnText}</button>
          </form>
        </div>
      </section>

      <section id="vision" className="vision-section">
        <div className="vision-content">
          <div className="section-title" style={{marginBottom: '30px', fontSize: 'clamp(24px, 5vw, 30px)', border: 'none', textShadow: 'none'}}>{general.visionTitle || "OUR VISION"}</div>
          <p>{general.visionText1 || "Vortex Originals (VRX) is not just an Esports organization; it is a movement."}</p>
          {general.visionText2 && <p>{general.visionText2}</p>}
          <div className="vision-tagline">{general.visionTagline || "#VORTEXORIGINALS"}</div>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-title">COMMUNICATIONS</div>
        <div className="social-links">
          <a href="https://x.com/VRXORIGINALS" target="_blank" rel="noreferrer" className="social-btn">TWITTER / X</a>
          <a href="https://www.instagram.com/vrxoriginals/" target="_blank" rel="noreferrer" className="social-btn">INSTAGRAM</a>
        </div>
        <div className="copyright">SYSTEM ONLINE: VORTEX ORIGINALS VRX &copy; 2026</div>
      </footer>
    </motion.div>
  );
}


// ==========================================
// 🚀 YOUTUBE FEED ROSTER PAGE (WITH LIVE DETECT)
// ==========================================
function RosterPage({ data, loading }) {
  const creators = useMemo(() => data.roster || [], [data.roster]);
  const youtubeCache = data.youtubeCache || {};
  const liveCache = data.liveCache || {}; 
  
  const [activeCreatorId, setActiveCreatorId] = useState("ALL");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [playingMobile, setPlayingMobile] = useState(null);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();
  const general = data.general || {};

  const navHeight = general.newsTicker ? 105 : 70; 

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // 🚀 ASSEMBLE LIVE FEED (MIX MANUAL + AUTO)
  const liveFeed = useMemo(() => {
      let activeLiveList = [];
      creators.forEach(c => {
          if (c.isLive === "true" || liveCache[c.id]) {
              // If auto-detected live, use API data. Else use fallback data from DB
              let videoData = liveCache[c.id] || {
                  videoId: c.featuredVideo ? getEmbedUrl(c.featuredVideo)?.split('embed/')[1]?.split('?')[0] : '',
                  title: `${c.name} is LIVE NOW!`,
                  thumbnail: c.img,
                  channelName: c.name,
                  link: c.youtube || c.link
              };
              activeLiveList.push({ ...videoData, priority: c.livePriority || 10, creatorId: c.id });
          }
      });
      return activeLiveList.sort((a, b) => a.priority - b.priority);
  }, [creators, liveCache]);

  // ASSEMBLE REGULAR YOUTUBE FEED
  let feedVideos = [];
  if (activeCreatorId === "ALL") {
      Object.keys(youtubeCache).forEach(key => {
          feedVideos = [...feedVideos, ...youtubeCache[key]];
      });
  } else if (activeCreatorId !== "LIVE") {
      feedVideos = youtubeCache[activeCreatorId] || [];
  }
  feedVideos.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));

  const handleVideoTap = (e, id) => {
      e.stopPropagation();
      setPlayingMobile(playingMobile === id ? null : id);
  };

  return (
    <motion.div className="app-container roster-page-wrapper" variants={pageVariants} initial="initial" animate="animate" exit="exit" style={{paddingTop: `${navHeight}px`, paddingBottom: '0'}}>
      
      {general.newsTicker && (
        <div className="news-ticker"><div className="ticker-content"><span>⚡ {general.newsTicker} ⚡</span><span>⚡ {general.newsTicker} ⚡</span><span>⚡ {general.newsTicker} ⚡</span></div></div>
      )}

      <nav className="navbar" style={{position: 'fixed', top: general.newsTicker ? '35px' : '0'}}>
        <div className="logo" onClick={() => navigate('/')} style={{cursor: 'pointer'}}>VORTEX<span style={{color: '#ff0000'}}> </span>ORIGINALS<div className="live-dot"></div></div>
        
        <div className={`hamburger ${isMenuOpen ? 'active' : ''}`} onClick={() => setIsMenuOpen(!isMenuOpen)}>
           <span></span><span></span><span></span>
        </div>

        <div className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
          <span onClick={() => navigate('/')}>RETURN TO HQ</span>
        </div>
      </nav>

      {/* TOP SCROLLABLE AVATAR BAR */}
      <div className="avatar-nav-bar" style={{ top: `${navHeight}px` }}>
          <div 
             className={`avatar-item ${activeCreatorId === "ALL" ? 'active' : ''}`} 
             onClick={() => setActiveCreatorId("ALL")}
          >
             <div className="avatar-circle" style={{background: '#222', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                 <span style={{color: '#fff', fontWeight: 'bold'}}>ALL</span>
             </div>
             <span className="avatar-name">Latest Feed</span>
          </div>

          {/* 🔴 LIVE RN BUTTON */}
          <div 
             className={`avatar-item ${activeCreatorId === "LIVE" ? 'active' : ''}`} 
             onClick={() => setActiveCreatorId("LIVE")}
          >
             <div className="avatar-circle live-pulse-circle">
                 <span style={{fontSize: '24px'}}>🔴</span>
             </div>
             <span className="avatar-name" style={{color: '#ff0000'}}>LIVE RN</span>
          </div>

          {creators.map(creator => (
              <div 
                 key={creator.id} 
                 className={`avatar-item ${activeCreatorId === creator.id ? 'active' : ''}`}
                 onClick={() => setActiveCreatorId(creator.id)}
              >
                 <div className="avatar-circle">
                     <img src={creator.img} alt={creator.name} />
                 </div>
                 <span className="avatar-name">{creator.name}</span>
              </div>
          ))}
      </div>

      {/* VERTICAL VIDEO FEED */}
      <section className="youtube-feed-section">
         
         <div className="section-title" style={{marginBottom: '40px', color: activeCreatorId === 'LIVE' ? '#ff0000' : 'white'}}>
             {activeCreatorId === "ALL" ? "LATEST TRANSMISSIONS" : 
              activeCreatorId === "LIVE" ? "🔴 ACTIVE OPERATIONS" : "AGENT ARCHIVES"}
         </div>

         {loading ? (
             <div className="yt-feed-container">
               <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
             </div>
         ) : activeCreatorId === "LIVE" ? (
             /* 🚀 RENDERING THE LIVE FEED */
             liveFeed.length === 0 ? (
                <div style={{textAlign: 'center', padding: '100px 20px', color: '#ff0000'}}>
                    <p style={{fontSize: '20px', fontWeight: 'bold'}}>🔴 NO AGENT IS LIVE RIGHT NOW.</p>
                    <p style={{fontSize: '12px', color: '#888'}}>Check back later for active transmissions.</p>
                </div>
             ) : (
                <motion.div className="yt-feed-container" variants={staggerContainer} initial="hidden" animate="show" key="live">
                    {liveFeed.map((video, index) => (
                        <motion.div 
                          key={`live-${video.videoId || index}`} 
                          className="yt-video-card" style={{borderColor: '#ff0000', boxShadow: '0 0 15px rgba(255,0,0,0.2)'}}
                          variants={staggerItem}
                          onClick={() => window.open(video.link || `https://youtube.com/watch?v=${video.videoId}`, '_blank')}
                        >
                           <div className="yt-thumbnail-wrapper">
                              <div className="live-badge" style={{position:'absolute', top: '10px', left: '10px', zIndex: 10}}>LIVE</div>
                              <img src={video.thumbnail} alt={video.title} />
                              <div className="yt-play-btn"><i className="fas fa-play"></i></div>
                           </div>
                           <div className="yt-card-info">
                              <img className="yt-channel-icon" src={creators.find(c => c.name === video.channelName)?.img || "https://via.placeholder.com/40"} alt="" style={{border: '2px solid red'}}/>
                              <div>
                                 <h3 className="yt-title" style={{color: 'red'}}>{video.title}</h3>
                                 <div className="yt-meta">
                                    <span>{video.channelName}</span>
                                    <span style={{margin: '0 5px'}}>•</span>
                                    <span>Streaming Now</span>
                                 </div>
                              </div>
                           </div>
                        </motion.div>
                     ))}
                </motion.div>
             )
         ) : feedVideos.length === 0 && activeCreatorId !== "LIVE" ? (
             <div style={{textAlign: 'center', padding: '100px 20px', color: '#888'}}>
                 <p style={{fontSize: '18px', fontWeight: 'bold', color: 'white'}}>NO VIDEO TRANSMISSIONS FOUND.</p>
             </div>
         ) : (
             <motion.div className="yt-feed-container" variants={staggerContainer} initial="hidden" animate="show" key={activeCreatorId}>
                
                {/* MIXING CREATOR PROFILE CARD IF SPECIFIC CREATOR IS SELECTED */}
                {activeCreatorId !== "ALL" && creators.filter(c => c.id === activeCreatorId).map((creator) => {
                   const isHovered = hoveredCard === creator.id || playingMobile === creator.id;
                   const mediaUrl = creator.hoverVideo ? getEmbedUrl(creator.hoverVideo) : null;
                   const isYouTube = mediaUrl && mediaUrl.includes('youtube.com/embed');

                   return (
                     <motion.div 
                       key={`profile-${creator.id}`} 
                       className="creator-card-new"
                       variants={staggerItem}
                       onMouseEnter={() => !isMobile && setHoveredCard(creator.id)}
                       onMouseLeave={() => !isMobile && setHoveredCard(null)}
                       onClick={() => setSelectedCreator(creator)}
                     >
                        <div className="creator-img-wrapper">
                           {isHovered && mediaUrl ? (
                               isYouTube ? (
                                   <iframe src={mediaUrl} className="hover-video-element" frameBorder="0" allow="autoplay; encrypted-media" style={{pointerEvents: 'none'}} title="hover-preview" />
                               ) : (
                                   <video src={mediaUrl} autoPlay loop muted playsInline className="hover-video-element" />
                               )
                           ) : (
                               <img src={creator.img} alt={creator.name} />
                           )}
                           {isMobile && creator.hoverVideo && playingMobile !== creator.id && (
                              <div className="play-icon-overlay" onClick={(e) => handleVideoTap(e, creator.id)}>▶</div>
                           )}
                        </div>
                        <div className="creator-info">
                           <h3>{creator.name}</h3>
                           <span>// VIEW FULL PROFILE</span>
                        </div>
                     </motion.div>
                   );
                })}

                {feedVideos.map((video, index) => (
                   <motion.div 
                     key={`${video.videoId}-${index}`} 
                     className="yt-video-card"
                     variants={staggerItem}
                     onClick={() => window.open(`https://youtube.com/watch?v=${video.videoId}`, '_blank')}
                   >
                      <div className="yt-thumbnail-wrapper">
                         <img src={video.thumbnail} alt={video.title} />
                         <div className="yt-play-btn"><i className="fas fa-play"></i></div>
                      </div>
                      <div className="yt-card-info">
                         <img className="yt-channel-icon" src={creators.find(c => c.name === video.channelName)?.img || "https://via.placeholder.com/40"} alt="" />
                         <div>
                            <h3 className="yt-title">{video.title}</h3>
                            <div className="yt-meta">
                               <span>{video.channelName}</span>
                               <span style={{margin: '0 5px'}}>•</span>
                               <span>{timeAgo(video.publishedAt)}</span>
                            </div>
                         </div>
                      </div>
                   </motion.div>
                ))}
             </motion.div>
         )}
      </section>

      {/* CREATOR PROFILE MODAL */}
      <AnimatePresence>
        {selectedCreator && (
            <motion.div 
              className="creator-modal-overlay"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setSelectedCreator(null)}
            >
               <motion.div 
                 className="creator-modal-content"
                 initial={{ scale: 0.8, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.8, y: 50 }}
                 onClick={(e) => e.stopPropagation()}
               >
                  <span className="close-modal" onClick={() => setSelectedCreator(null)}>&times;</span>
                  
                  <div className="modal-header">
                     <img src={selectedCreator.img} alt={selectedCreator.name} className="modal-img" />
                     <div>
                        <h2 style={{fontFamily: 'Orbitron', margin: 0, fontSize: 'clamp(24px, 6vw, 32px)', color: 'var(--neon-red)'}}>{selectedCreator.name}</h2>
                        <span style={{color: 'var(--text-sub)', fontWeight: 'bold', letterSpacing: '2px'}}>{selectedCreator.category}</span>
                        
                        <div className="modal-socials" style={{marginTop: '10px'}}>
                           {selectedCreator.youtube && <a href={selectedCreator.youtube} target="_blank" rel="noreferrer"><i className="fab fa-youtube"></i></a>}
                           {selectedCreator.instagram && <a href={selectedCreator.instagram} target="_blank" rel="noreferrer"><i className="fab fa-instagram"></i></a>}
                           {selectedCreator.link && <a href={selectedCreator.link} target="_blank" rel="noreferrer"><i className="fas fa-link"></i></a>}
                        </div>
                     </div>
                  </div>

                  <p className="modal-desc" style={{lineHeight: '1.6', color: '#ccc', paddingBottom: '20px', borderBottom: '1px solid #333'}}>
                     {selectedCreator.description || "No transmission logs available for this agent yet."}
                  </p>

                  {selectedCreator.featuredVideo && (
                     <div className="modal-video-container">
                        <iframe 
                           width="100%" height="100%" 
                           src={getEmbedUrl(selectedCreator.featuredVideo) || selectedCreator.featuredVideo} 
                           title="Featured Video" 
                           frameBorder="0" 
                           allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                           allowFullScreen>
                        </iframe>
                     </div>
                  )}
               </motion.div>
            </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}


// ==========================================
// PARTNERS PAGE COMPONENT 
// ==========================================
function PartnersPage({ data }) {
    const partners = data.partners || [];
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const navigate = useNavigate();
    const general = data.general || {};

    const navHeight = general.newsTicker ? 105 : 70; 
  
    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);
  
    return (
      <motion.div className="app-container roster-page-wrapper" variants={pageVariants} initial="initial" animate="animate" exit="exit" style={{ paddingTop: `${navHeight}px` }}>
         
         {general.newsTicker && (
           <div className="news-ticker"><div className="ticker-content"><span>⚡ {general.newsTicker} ⚡</span><span>⚡ {general.newsTicker} ⚡</span><span>⚡ {general.newsTicker} ⚡</span></div></div>
         )}

         <nav className="navbar" style={{position: 'fixed', top: general.newsTicker ? '35px' : '0'}}>
          <div className="logo" onClick={() => navigate('/')} style={{cursor: 'pointer'}}>VORTEX<span style={{color: '#ff0000'}}> </span>ORIGINALS<div className="live-dot"></div></div>
          
          <div className={`hamburger ${isMenuOpen ? 'active' : ''}`} onClick={() => setIsMenuOpen(!isMenuOpen)}>
             <span></span><span></span><span></span>
          </div>

          <div className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
            <span onClick={() => navigate('/')}>RETURN TO HQ</span>
          </div>
        </nav>
  
        <section className="roster-page" style={{paddingBottom: '100px'}}>
           <div className="section-title" style={{marginTop: '60px', borderColor: '#fff', textShadow: '0 0 20px rgba(255,255,255,0.5)'}}>INDUSTRY LEADERS</div>
           <p style={{color: 'var(--text-sub)', marginBottom: '50px', letterSpacing: '2px', textTransform: 'uppercase', fontSize: '14px'}}>POWERING THE NEXT GENERATION OF ESPORTS</p>
           
           <motion.div 
             className="partners-grid-new"
             variants={staggerContainer}
             initial="hidden"
             animate="show"
           >
              {partners.length === 0 ? <p style={{color: '#888'}}>No partner data found.</p> : null}
              {partners.map((p) => (
                 <motion.div 
                   key={p.id} 
                   className="partner-card-new"
                   variants={staggerItem}
                   onClick={() => window.open(p.link, '_blank')}
                 >
                    <div className="partner-logo-box">
                       <img src={p.img} alt={p.name} />
                    </div>
                    <div className="partner-details">
                       <h3>{p.name}</h3>
                       <p>{p.role || "Official Partner of Vortex Originals."}</p>
                       <span className="visit-btn">VISIT WEBSITE ↗</span>
                    </div>
                 </motion.div>
              ))}
           </motion.div>
        </section>
      </motion.div>
    );
  }

// --- MAIN APP ROUTER & BACKGROUND CONTROLLER ---
function AnimatedRoutes() {
  const location = useLocation();
  const [data, setData] = useState({ roster: [], owners: [], collaborators: [], events: [], partners: [], general: {}, youtubeCache: {}, liveCache: {} });
  const [backgrounds, setBackgrounds] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/data`).then(res => {
        setData(res.data);
        if(res.data.backgrounds) {
            const newBgs = {};
            if(res.data.backgrounds['HOME']) {
                const bg = res.data.backgrounds['HOME'];
                newBgs['HOME'] = (typeof bg === 'string') ? { type: 'image', url: bg } : bg;
            }
            setBackgrounds(newBgs);
        }
        setLoading(false);
    }).catch(err => {
        console.error("Server error");
        setLoading(false);
    });
  }, []);

  const isAdmin = location.pathname === '/admin';

  return (
    <>
      {!isAdmin && (
         <>
           <div className="bg-engine">
             <div className="game-bg-layer active">
                 {backgrounds['HOME']?.type === 'video' ? (
                     <video autoPlay loop muted playsInline className="bg-video">
                         <source src={backgrounds['HOME'].url} type="video/mp4" />
                     </video>
                 ) : (
                     <div className="bg-image" style={{ backgroundImage: `url(${backgrounds['HOME']?.url || ''})` }} />
                 )}
             </div>
          </div>
          <div className="star-field"></div>
         </>
      )}

      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<Home data={data} loading={loading} />} />
          <Route path="/roster" element={<RosterPage data={data} loading={loading} />} />
          <Route path="/partners" element={<PartnersPage data={data} />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </AnimatePresence>
    </>
  );
}

function App() {
  return (
    <Router>
      <AnimatedRoutes />
    </Router>
  );
}

export default App;